import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfFont,
  PdfBrush,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';
import '../../pdf/draw/cut';
import '../../pdf/draw/transforms';

export abstract class Los implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  protected abstract drawCellContent(
    page: PdfGen['getPage'] extends (...args: any[]) => infer R ? R : never,
    opts: OptsLabel,
    globalIdx: number,
    rect0: PdfRect,
    fonts?: { leftFont?: PdfFont; rightFont?: PdfFont; commonFont?: PdfFont }
  ): void;

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const margin1 = PdfUnit.FromMillimeters(10);
    const numWidth = PdfUnit.FromMillimeters(10);

    const cellWidth = (PdfSize.A4.width - 2 * margin) / 2;
    const cellHeight = (PdfSize.A4.height - 2 * margin) / 5;
    const labelWidth = cellWidth - 2 * margin1 - numWidth;
    const rect1Size = new PdfSize(labelWidth, cellHeight - 2 * margin1);
    const halfRectSize = new PdfSize(labelWidth / 2, cellHeight - 2 * margin1);

    const itemsPerPage = 10;
    const totalItems = opts.list.length;
    const pageCount = Math.ceil(totalItems / itemsPerPage);

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
      const page = pdf.addPage();

      const startIdx = pageIndex * itemsPerPage;
      const endIdx = Math.min(startIdx + itemsPerPage, totalItems);

      const leftTexts = opts.list.slice(startIdx, endIdx).map((item) => item.left.getText() ?? '');
      const leftFont = page.adjustFontMulti(
        leftTexts.map((t) => [t]),
        GUtil.baseFont,
        rect1Size
      );

      const rightTexts = opts.list
        .slice(startIdx, endIdx)
        .map((item) => item.right.getText() ?? '');
      const rightFont =
        rightTexts.length > 0
          ? page.adjustFontMulti(
              rightTexts.map((t) => [t]),
              GUtil.baseFont,
              rect1Size
            )
          : undefined;

      const allTexts = [...leftTexts, ...rightTexts];
      const commonFont =
        allTexts.length > 0
          ? page.adjustFontMulti(
              allTexts.map((t) => [t]),
              GUtil.baseFont,
              halfRectSize
            )
          : undefined;

      for (let x = 0; x < 2; x++) {
        for (let y = 0; y < 5; y++) {
          const localIdx = x * 5 + y;
          const globalIdx = startIdx + localIdx;
          if (globalIdx >= totalItems) continue;

          const rect = new PdfRect(
            margin + x * cellWidth,
            margin + y * cellHeight,
            cellWidth,
            cellHeight
          );
          const rect0 = new PdfRect(
            rect.x + margin1 + numWidth,
            rect.y + margin1,
            rect.width - 2 * margin1 - numWidth,
            rect.height - 2 * margin1
          );
          const rectN = new PdfRect(
            rect.x + margin1,
            rect.y + margin1,
            numWidth,
            rect.height - 2 * margin1
          );

          this.drawCellContent(page, opts, globalIdx, rect0, { leftFont, rightFont, commonFont });

          page.drawRectangle(GUtil.cutPen, null, rect);
          page.drawRectangle(GUtil.basePen, null, rect0);

          const center = new PdfPoint(rectN.centerX, rectN.centerY);
          page.save();
          page.rotateAtTransform(270, center);
          const numStr = `#${(globalIdx + 1).toString().padStart(2, '0')}`;
          page.drawString(numStr, GUtil.baseFont, PdfBrush.Black, null, rectN, ETextAlign.Center);
          page.restore();
          page.drawRectangle(GUtil.basePen, null, rectN);
        }
      }
    }
  }
}

export class Los__Zaklad extends Los {
  protected drawCellContent(
    page: any,
    opts: OptsLabel,
    globalIdx: number,
    rect0: PdfRect,
    fonts?: { leftFont?: PdfFont; rightFont?: PdfFont; commonFont?: PdfFont }
  ): void {
    const rect1 = new PdfRect(rect0.x, rect0.y, rect0.width, rect0.height);
    page.drawLabel(opts.list[globalIdx].left, { rect: rect1, font: fonts?.leftFont });
  }
}

export class Los__VedleSebe extends Los {
  protected drawCellContent(
    page: any,
    opts: OptsLabel,
    globalIdx: number,
    rect0: PdfRect,
    fonts?: { leftFont?: PdfFont; rightFont?: PdfFont; commonFont?: PdfFont }
  ): void {
    const rect1 = new PdfRect(rect0.x, rect0.y, rect0.width / 2, rect0.height);
    const rect2 = new PdfRect(rect0.x + rect0.width / 2, rect0.y, rect0.width / 2, rect0.height);

    page.drawLabel(opts.list[globalIdx].left, { rect: rect1, font: fonts?.commonFont });
    page.drawLabel(opts.list[globalIdx].right, { rect: rect2, font: fonts?.commonFont });
  }
}
